/**
 * Generate a PNG data URL for an initials avatar that mirrors the chat
 * UI's `<Avatar>` fallback (same palette, same hash, same up-to-2-letter
 * initials). Used as the OS-notification `icon` when the sender has no
 * `avatar_url`, so the desktop banner matches what's rendered in-app
 * instead of falling through to the 3mote logo.
 *
 * Rendered to a 128px canvas (2× nominal density) and emitted as
 * `image/png` — Chrome on macOS doesn't reliably accept SVG data URLs
 * for `new Notification({ icon })`, but PNG data URLs work everywhere
 * the Notification API is supported.
 */

// Hex equivalents of the Tailwind `bg-{color}-700` palette in
// `src/components/Avatar.tsx`. Keep in sync with that file's
// `AVATAR_BG_PALETTE`.
const PALETTE_HEX = [
  '#0069A8', // sky
  '#007A55', // emerald
  '#7008E7', // violet
  '#BB4D00', // amber
  '#C70036', // rose
  '#00786F', // teal
] as const

const SIZE_PX = 128

function hashColor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  const idx = Math.abs(hash) % PALETTE_HEX.length
  return PALETTE_HEX[idx] ?? PALETTE_HEX[0]
}

function initialsOf(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  return trimmed
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}

export function initialsIconDataUrl(name: string, seed?: string): string | null {
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = SIZE_PX
  canvas.height = SIZE_PX
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.fillStyle = hashColor(seed ?? name)
  ctx.beginPath()
  ctx.arc(SIZE_PX / 2, SIZE_PX / 2, SIZE_PX / 2, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = '#FFFFFF'
  ctx.font = `bold ${Math.round(SIZE_PX * 0.42)}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // Nudge baseline ~4% down to optical-center single + double initials
  // — matches what the in-app Avatar achieves via flex centering.
  ctx.fillText(initialsOf(name), SIZE_PX / 2, SIZE_PX / 2 + SIZE_PX * 0.04)

  return canvas.toDataURL('image/png')
}
